import axios from "axios";
import { useState } from "react";
import { toast } from "sonner";
import { Search, ShieldOff, ShieldCheck, StickyNote } from "lucide-react";
import { API } from "./shared";

const cell = { padding: "6px 9px", borderBottom: "1px solid #e5e7eb", fontSize: 12, textAlign: "left", verticalAlign: "top" };
const fmt = (d) => (d ? new Date(d).toLocaleString("en-IN") : "—");

function Actions({ u, onChange }) {
  const [mode, setMode] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "suspend") await axios.post(`${API}/admin/users/${u.id}/suspend`, { reason: text });
      else await axios.post(`${API}/admin/users/${u.id}/notes`, { text });
      toast.success(mode === "suspend" ? "Account suspended" : "Note added");
      setMode(null); setText(""); onChange();
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Action failed"); } finally { setBusy(false); }
  };
  const unsuspend = async () => {
    try { await axios.post(`${API}/admin/users/${u.id}/unsuspend`); toast.success("Account reinstated"); onChange(); }
    catch { toast.error("Could not reinstate"); }
  };
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {u.role !== "admin" && (u.is_suspended
          ? <button className="secondary-btn" data-testid={`leak-unsuspend-${u.id}`} onClick={unsuspend}><ShieldCheck size={13}/> Reinstate account</button>
          : <button className="primary-btn" style={{ background: "#B91C1C" }} data-testid={`leak-suspend-${u.id}`} onClick={() => setMode("suspend")}><ShieldOff size={13}/> Suspend account</button>)}
        <button className="secondary-btn" data-testid={`leak-note-${u.id}`} onClick={() => setMode("note")}><StickyNote size={13}/> Add internal note</button>
      </div>
      {mode && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input data-testid={`leak-${mode}-input-${u.id}`} value={text} onChange={e => setText(e.target.value)} autoFocus
            placeholder={mode === "suspend" ? "Reason for suspension (shown in audit log)" : "Internal note (admins only)"}
            style={{ flex: 1, minWidth: 260, border: "1px solid #CBD5E1", borderRadius: 7, padding: "7px 10px", fontSize: 12.5 }}/>
          <button className="primary-btn" data-testid={`leak-${mode}-submit-${u.id}`} disabled={busy || text.trim().length < (mode === "suspend" ? 3 : 1)} onClick={submit}>
            {mode === "suspend" ? "Confirm suspend" : "Save note"}
          </button>
          <button className="m-ghost" onClick={() => { setMode(null); setText(""); }}>Cancel</button>
        </div>
      )}
    </div>
  );
}

function Result({ r, onChange }) {
  const u = r.user;
  return (
    <div data-testid={`leak-result-${u.id}`} style={{ border: `1px solid ${r.matched_codes.length ? "#FCA5A5" : "#E2E8F0"}`, borderRadius: 10, padding: 14, display: "grid", gap: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <b style={{ fontSize: 15 }}>{u.name || "—"}</b>{u.company ? ` · ${u.company}` : ""}
          {u.is_suspended && <span data-testid={`leak-suspended-badge-${u.id}`} style={{ marginLeft: 8, background: "#FEE2E2", color: "#B91C1C", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999 }}>SUSPENDED</span>}
          <div style={{ fontSize: 12, color: "#64748b" }}>{u.email} · {u.phone || "no phone"} · {u.role} · ID {u.id} · joined {fmt(u.created_at)}</div>
          {r.matched_codes.length > 0 && <div style={{ fontSize: 12, color: "#B91C1C", fontWeight: 600 }} data-testid={`leak-matched-${u.id}`}>Matched code: {r.matched_codes.join(", ")}</div>}
          {u.is_suspended && u.suspended_reason && <div style={{ fontSize: 12, color: "#B91C1C" }}>Suspended {fmt(u.suspended_at)}: {u.suspended_reason}</div>}
        </div>
        <Actions u={u} onChange={onChange}/>
      </div>
      {u.internal_notes?.length > 0 && (
        <div data-testid={`leak-notes-${u.id}`} style={{ background: "#F8FAFC", borderRadius: 8, padding: "8px 10px", fontSize: 12 }}>
          {u.internal_notes.slice().reverse().map(n => <div key={n.id}><b>{n.by}</b> · {fmt(n.at)}: {n.text}</div>)}
        </div>
      )}
      <div style={{ maxHeight: 260, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#f3f4f6" }}>{["Ref code", "Opened", "Guide", "Pages viewed", "IP", "Device"].map(h => <th key={h} style={cell}>{h}</th>)}</tr></thead>
          <tbody>{r.views.map(v => (
            <tr key={v.id} style={{ background: r.matched_codes.includes(v.id) ? "#FEF2F2" : undefined }} data-testid={`leak-view-${v.id}`}>
              <td style={{ ...cell, fontFamily: "DM Mono, monospace", fontWeight: 700 }}>{v.id}</td>
              <td style={cell}>{fmt(v.at)}</td>
              <td style={cell}>{v.role}</td>
              <td style={cell}>{(v.pages_viewed || []).slice().sort((a, b) => a - b).join(", ") || "—"}</td>
              <td style={cell} title={v.ip}>{(v.ip || "—").split(",")[0]}</td>
              <td style={{ ...cell, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={v.ua}>{v.ua || "—"}</td>
            </tr>))}
            {!r.views.length && <tr><td style={cell} colSpan={6}>This account has never opened a guide.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AcLeakLookup() {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const search = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try { const r = await axios.get(`${API}/admin/leak-lookup`, { params: { q } }); setData(r.data); }
    catch (err) { toast.error(err.response?.data?.detail || "Lookup failed"); } finally { setBusy(false); }
  };
  return (
    <div className="ac-panel" data-testid="ac-leak-lookup">
      <header className="ac-panel-head"><b style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Search size={15}/> Leak lookup</b></header>
      <p style={{ fontSize: 12.5, color: "#64748b", margin: 0 }}>
        Paste the reference code from a leaked guide image (e.g. <b>GV1A2B3C4D</b> from the footer), or any part of it. If the footer is cropped,
        search by the user ID or the email visible in the diagonal watermark. Every lookup is recorded in the admin audit log.
      </p>
      <form onSubmit={search} style={{ display: "flex", gap: 8 }}>
        <input data-testid="leak-search-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Ref code, user ID or email"
          style={{ flex: 1, border: "1px solid #CBD5E1", borderRadius: 7, padding: "9px 11px", fontSize: 13 }}/>
        <button className="primary-btn" data-testid="leak-search-btn" disabled={busy || q.trim().length < 3}>{busy ? "Searching…" : "Find account"}</button>
      </form>
      {data && (data.results.length
        ? <div style={{ display: "grid", gap: 12 }} data-testid="leak-results">{data.results.map(r => <Result key={r.user.id} r={r} onChange={search}/>)}</div>
        : <div data-testid="leak-no-results" style={{ fontSize: 13, color: "#64748b" }}>No account or guide viewing matches "{data.query}".</div>)}
    </div>
  );
}
